/**
 * School admin → Staff.
 *
 * Adds admins and teachers, and — for a teacher — optionally what they take
 * and which class they look after, in the same breath. That is still stored
 * where it always was, per classroom (ClassroomTeacherScope, and the class
 * teacher on the classroom): this page and the classroom card are two views of
 * the same rows, and each teacher's "Classes" editor writes them through the
 * same server-side rules.
 *
 * Accounts CWY added — the school's first admin, or one CWY put in later — are
 * marked "Added by CWY" and offer no suspend or reset: only CWY can change
 * them. The server decides that per row (`manageable`), so this page never
 * offers a button the API would refuse.
 *
 * Staff credentials are self-managed. A school admin may issue a teacher a new
 * temporary password (and is audited doing it), never a fellow admin.
 */
import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiGet, apiPatch, apiPost, apiPut } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';
import { MEMBERSHIP_ROLE_LABEL } from '@cwy/config/contracts';
import type {
  ClassroomDTO,
  CurriculumSelectionDTO,
  StaffImportResultDTO,
  TeacherAssignment,
  TenantStaffMemberDTO,
} from '@cwy/config/contracts';
import {
  Button,
  Card,
  Field,
  Input,
  Select,
  StudentPage,
  StudentPageHeader,
} from '@/components/ds';
import { SecretReveal } from '@/components/admin/SecretReveal';
import { QueryState } from '@/components/admin/QueryState';
import { StaffImportCard, StaffImportResult } from '@/components/admin/StaffImport';
import {
  SubjectUnitsPicker,
  isCompleteSubjectUnits,
  useOfferedSubjects,
} from '@/components/admin/ClassroomsManager';

interface CreatedStaff {
  userId: string;
  membershipId: string;
  temporaryPassword: string | null;
}

// What this form can CREATE — not every role the list below can display. An
// existing COUNSELLOR still renders and can still have their password reset; the
// role just has no console of its own yet, so nobody makes a new one.
const STAFF_ROLES = ['TEACHER', 'SCHOOL_ADMIN'] as const;

/** A teacher's classes as the form holds them. */
interface ClassesValue {
  assignments: TeacherAssignment[];
  classTeacherOf: string | null;
}

const NO_CLASSES: ClassesValue = { assignments: [], classTeacherOf: null };

/** The server's scope rows, regrouped into the form's one-row-per-subject shape. */
function classesOf(member: TenantStaffMemberDTO): ClassesValue {
  const grouped = new Map<string, TeacherAssignment>();
  for (const row of member.assignments) {
    const key = `${row.classroomId}:${row.subjectId}`;
    const held = grouped.get(key);
    if (!held) {
      grouped.set(key, {
        classroomId: row.classroomId,
        subjectId: row.subjectId,
        unitIds: row.unitId === null ? null : [row.unitId],
      });
    } else if (held.unitIds !== null) {
      held.unitIds = row.unitId === null ? null : [...held.unitIds, row.unitId];
    }
  }
  return {
    assignments: [...grouped.values()],
    classTeacherOf: member.classTeacherOf[0]?.id ?? null,
  };
}

const classesComplete = (value: ClassesValue) =>
  value.assignments.every((row) => Boolean(row.classroomId) && isCompleteSubjectUnits(row));

/** What the request carries — only the rows that say something. */
const classesPayload = (value: ClassesValue) => ({
  assignments: value.assignments.filter((row) => row.classroomId && row.subjectId),
  classTeacherOf: value.classTeacherOf,
});

export function SchoolStaffPage() {
  const staff = useQuery({
    queryKey: ['tenant-admin', 'staff'],
    queryFn: () => apiGet<TenantStaffMemberDTO[]>('/tenant-admin/staff'),
  });
  const classrooms = useQuery({
    queryKey: ['tenant-admin', 'classrooms'],
    queryFn: () => apiGet<ClassroomDTO[]>('/tenant-admin/classrooms'),
  });
  const selection = useQuery({
    queryKey: ['tenant-admin', 'curriculum'],
    queryFn: () => apiGet<CurriculumSelectionDTO>('/tenant-admin/curriculum'),
  });
  const offered = useMemo(() => new Set(selection.data?.chapterIds ?? []), [selection.data]);
  const rooms = classrooms.data ?? [];

  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [imported, setImported] = useState<StaffImportResultDTO | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<(typeof STAFF_ROLES)[number]>('TEACHER');
  const [classes, setClasses] = useState<ClassesValue>(NO_CLASSES);
  const [editing, setEditing] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [created, setCreated] = useState<{ result: CreatedStaff; email: string } | null>(null);
  const [reset, setReset] = useState<{ email: string; secret: string } | null>(null);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['tenant-admin', 'staff'] });
    void queryClient.invalidateQueries({ queryKey: ['tenant-admin', 'classrooms'] });
  };

  const createStaff = useMutation({
    mutationFn: () =>
      apiPost<CreatedStaff>('/tenant-admin/staff', {
        name: name.trim(),
        email: email.trim(),
        role,
        ...(role === 'TEACHER' ? classesPayload(classes) : {}),
      }),
    onSuccess: (result) => {
      setCreated({ result, email: email.trim() });
      setShowForm(false);
      setName('');
      setEmail('');
      setClasses(NO_CLASSES);
      invalidate();
    },
    onError: (err: Error) => setError(err.message),
  });

  const setStatus = useMutation({
    mutationFn: ({ membershipId, status }: { membershipId: string; status: string }) =>
      apiPatch(`/tenant-admin/staff/${membershipId}`, { status }),
    onSuccess: invalidate,
    onError: (err: Error) => setError(err.message),
  });

  const resetPassword = useMutation({
    mutationFn: (member: TenantStaffMemberDTO) =>
      apiPost<{ temporaryPassword: string }>(
        `/tenant-admin/staff/${member.membershipId}/reset-password`,
        { reason: 'Reset by a school admin' },
      ),
    onSuccess: (result, member) => {
      setReset({ email: member.email, secret: result.temporaryPassword });
      invalidate();
    },
    onError: (err: Error) => setError(err.message),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError('');
    if (name.trim() && email.trim() && (role !== 'TEACHER' || classesComplete(classes))) {
      createStaff.mutate();
    }
  };

  return (
    <StudentPage>
      <StudentPageHeader
        title="Staff"
        subtitle="Teachers and admins at your school, and the classes each teacher takes."
        action={
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setShowForm(false);
                setShowImport((v) => !v);
              }}
            >
              {showImport ? 'Cancel import' : 'Import a CSV'}
            </Button>
            <Button
              onClick={() => {
                setShowImport(false);
                setShowForm((v) => !v);
              }}
            >
              {showForm ? 'Cancel' : 'Add a staff member'}
            </Button>
          </div>
        }
      />

      {imported && <StaffImportResult result={imported} onDone={() => setImported(null)} />}

      {showImport && (
        <StaffImportCard
          rooms={rooms}
          offered={offered}
          onImported={(result) => {
            setImported(result);
            setShowImport(false);
            invalidate();
          }}
        />
      )}

      {reset && (
        <SecretReveal
          title="New temporary password"
          identifier={reset.email}
          secret={reset.secret}
          onDismiss={() => setReset(null)}
        />
      )}

      {created &&
        (created.result.temporaryPassword ? (
          <SecretReveal
            title="Staff member added — give them these details"
            identifier={created.email}
            secret={created.result.temporaryPassword}
            onDismiss={() => setCreated(null)}
          />
        ) : (
          // No password when an existing CWY account was attached: that person
          // already has one, and this school has no authority to change it.
          <Card padding="14px 16px">
            <p className="text-sm text-ink">
              <strong>{created.email}</strong> already has a CWY account and has been added to your
              school. They sign in with their existing password.
            </p>
            <div className="mt-3">
              <Button variant="secondary" onClick={() => setCreated(null)}>
                Got it
              </Button>
            </div>
          </Card>
        ))}

      {error && <p className="text-sm text-danger">{error}</p>}

      {showForm && (
        <Card title="Add a staff member">
          <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
            <div className="flex flex-wrap gap-3">
              <Field label="Full name" className="min-w-[200px] flex-1" required>
                {({ id }) => (
                  <Input
                    id={id}
                    autoFocus
                    value={name}
                    maxLength={120}
                    onChange={(e) => setName(e.target.value)}
                  />
                )}
              </Field>
              <Field label="Work email" className="min-w-[240px] flex-1" required>
                {({ id }) => (
                  <Input
                    id={id}
                    type="email"
                    value={email}
                    maxLength={255}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                )}
              </Field>
            </div>

            <Field
              label="Role"
              hint="Admins manage staff, classes and the whole roster. Teachers see the classes they take."
            >
              {({ id }) => (
                <Select
                  id={id}
                  value={role}
                  onChange={(e) => setRole(e.target.value as (typeof STAFF_ROLES)[number])}
                >
                  {STAFF_ROLES.map((value) => (
                    <option key={value} value={value}>
                      {MEMBERSHIP_ROLE_LABEL[value]}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            {role === 'TEACHER' && (
              <div className="rounded-lg border border-line px-3 py-3">
                <p className="text-sm font-semibold text-ink">Classes (optional)</p>
                <p className="mb-3 text-xs text-ink-soft">
                  What they take, class by class. You can also do this later — here, or on each
                  classroom.
                </p>
                <ClassesFields
                  rooms={rooms}
                  offered={offered}
                  value={classes}
                  onChange={setClasses}
                />
              </div>
            )}

            <div>
              <Button
                type="submit"
                disabled={
                  createStaff.isPending || (role === 'TEACHER' && !classesComplete(classes))
                }
              >
                {createStaff.isPending ? 'Adding…' : 'Add staff member'}
              </Button>
            </div>
          </form>
        </Card>
      )}

      <QueryState
        isPending={staff.isPending}
        error={staff.error}
        isEmpty={staff.data?.length === 0}
        empty={<p className="text-sm text-ink-soft">No staff yet.</p>}
      >
        <div className="flex flex-col gap-2.5">
          {staff.data?.map((member) => {
            const teaches = member.role === 'TEACHER' || member.role === 'COUNSELLOR';
            return (
              <Card key={member.membershipId} padding="13px 16px">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-sm font-bold text-ink">{member.name}</span>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-fs-11 font-semibold text-ink-soft">
                        {MEMBERSHIP_ROLE_LABEL[member.role]}
                      </span>
                      {member.classTeacherOf.map((room) => (
                        <span
                          key={room.id}
                          className="rounded-full px-2 py-0.5 text-fs-11 font-semibold"
                          style={{
                            background: 'var(--bin-green-bg)',
                            color: 'var(--bin-green-deep)',
                          }}
                        >
                          Class teacher · {room.name}
                        </span>
                      ))}
                      {member.provisionedByPlatform && (
                        <span
                          className="rounded-full bg-slate-100 px-2 py-0.5 text-fs-11 font-semibold text-ink-soft"
                          title="CWY created this account. Only CWY can suspend, remove or reset it."
                        >
                          Added by CWY
                        </span>
                      )}
                      {member.status !== 'ACTIVE' && (
                        <span
                          className="rounded-full px-2 py-0.5 text-fs-11 font-semibold"
                          style={{
                            background: 'var(--bin-pink-bg)',
                            color: 'var(--bin-pink-deep)',
                          }}
                        >
                          {member.status === 'SUSPENDED' ? 'Suspended' : 'Left'}
                        </span>
                      )}
                      {member.pendingPasswordChange && (
                        <span
                          className="rounded-full px-2 py-0.5 text-fs-11 font-semibold"
                          style={{
                            background: 'var(--bin-orange-bg)',
                            color: 'var(--bin-orange-deep)',
                          }}
                        >
                          Not signed in yet
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 truncate text-xs text-ink-soft">
                      {member.email}
                      {member.subjects.length > 0
                        ? ` · Subject teacher: ${member.subjects.join(', ')}`
                        : member.role === 'TEACHER' && member.classTeacherOf.length === 0
                          ? ' · no classes yet'
                          : ''}
                      {member.classroomCount > 0 &&
                        ` · ${member.classroomCount} ${member.classroomCount === 1 ? 'class' : 'classes'}`}
                    </p>
                    {member.provisionedByPlatform && !member.manageable && (
                      <p className="mt-0.5 text-xs text-ink-faint">
                        Added by CWY — only CWY can suspend, remove or reset this account.
                      </p>
                    )}
                  </div>

                  <div className="flex flex-none items-center gap-2">
                    {teaches && member.status === 'ACTIVE' && (
                      <Button
                        variant="secondary"
                        onClick={() =>
                          setEditing(editing === member.membershipId ? null : member.membershipId)
                        }
                      >
                        {editing === member.membershipId ? 'Close' : 'Classes'}
                      </Button>
                    )}
                    {/* Teachers and counsellors only. A fellow admin is a peer, and
                    the endpoint refuses one — offering the button would be a dead
                    end. Nor on an account CWY added: that is CWY's to reset. */}
                    {teaches && member.manageable && (
                      <Button
                        variant="secondary"
                        disabled={resetPassword.isPending}
                        onClick={() => {
                          setError('');
                          if (
                            window.confirm(
                              `Reset ${member.name}'s password? They are signed out immediately and must set a new one before doing anything else.`,
                            )
                          ) {
                            resetPassword.mutate(member);
                          }
                        }}
                      >
                        Reset password
                      </Button>
                    )}
                    {member.manageable && (
                      <Button
                        variant="secondary"
                        disabled={setStatus.isPending}
                        onClick={() => {
                          const next = member.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
                          const message =
                            next === 'SUSPENDED'
                              ? `Suspend ${member.name}? They are signed out immediately and lose access to their classes. Their account and history are kept.`
                              : `Restore access for ${member.name}?`;
                          if (window.confirm(message)) {
                            setError('');
                            setStatus.mutate({ membershipId: member.membershipId, status: next });
                          }
                        }}
                      >
                        {member.status === 'ACTIVE' ? 'Suspend' : 'Restore'}
                      </Button>
                    )}
                  </div>
                </div>

                {editing === member.membershipId && (
                  <TeacherClassesEditor
                    key={member.membershipId}
                    member={member}
                    rooms={rooms}
                    offered={offered}
                    onSaved={() => {
                      setEditing(null);
                      invalidate();
                    }}
                    onError={setError}
                  />
                )}
              </Card>
            );
          })}
        </div>
      </QueryState>
    </StudentPage>
  );
}

/**
 * One teacher's classes, from their side — replace-in-full, like the classroom
 * card: what is on screen when they press Save is the whole answer.
 */
function TeacherClassesEditor({
  member,
  rooms,
  offered,
  onSaved,
  onError,
}: {
  member: TenantStaffMemberDTO;
  rooms: ClassroomDTO[];
  offered: ReadonlySet<string>;
  onSaved: () => void;
  onError: (message: string) => void;
}) {
  const [value, setValue] = useState<ClassesValue>(() => classesOf(member));
  const save = useMutation({
    mutationFn: () =>
      apiPut(`/tenant-admin/staff/${member.membershipId}/classes`, {
        ...classesPayload(value),
        // A counsellor cannot be a class teacher; the server refuses it, so the
        // form never sends one.
        classTeacherOf: member.role === 'TEACHER' ? value.classTeacherOf : null,
      }),
    onSuccess: onSaved,
    onError: (err: Error) => onError(err.message),
  });

  return (
    <div className="mt-4 flex flex-col gap-3 border-t border-line pt-4">
      <ClassesFields
        rooms={rooms}
        offered={offered}
        value={value}
        onChange={setValue}
        allowClassTeacher={member.role === 'TEACHER'}
      />
      <div>
        <Button
          disabled={save.isPending || !classesComplete(value)}
          onClick={() => {
            onError('');
            save.mutate();
          }}
        >
          {save.isPending ? 'Saving…' : 'Save classes'}
        </Button>
      </div>
    </div>
  );
}

/**
 * Rows of classroom · subject · units, and "class teacher of". Controlled —
 * the create form and the per-teacher editor both own the value.
 */
function ClassesFields({
  rooms,
  offered,
  value,
  onChange,
  allowClassTeacher = true,
}: {
  rooms: ClassroomDTO[];
  offered: ReadonlySet<string>;
  value: ClassesValue;
  onChange: (next: ClassesValue) => void;
  allowClassTeacher?: boolean;
}) {
  if (rooms.length === 0) {
    return (
      <p className="text-sm text-ink-soft">
        No classrooms yet — create them under Classrooms first.
      </p>
    );
  }

  const setRow = (index: number, row: TeacherAssignment) =>
    onChange({
      ...value,
      assignments: value.assignments.map((each, i) => (i === index ? row : each)),
    });

  return (
    <div className="flex flex-col gap-3">
      {value.assignments.map((row, index) => (
        <AssignmentRow
          // Index keys are right here: rows have no identity until saved.
          key={index}
          rooms={rooms}
          offered={offered}
          value={row}
          onChange={(next) => setRow(index, next)}
          onRemove={() =>
            onChange({ ...value, assignments: value.assignments.filter((_, i) => i !== index) })
          }
        />
      ))}
      <div>
        <Button
          variant="secondary"
          onClick={() =>
            onChange({
              ...value,
              assignments: [
                ...value.assignments,
                { classroomId: '', subjectId: '', unitIds: null },
              ],
            })
          }
        >
          Add a class
        </Button>
      </div>

      {allowClassTeacher && (
        <Field
          label="Class teacher of"
          className="max-w-[360px]"
          hint="A class teacher reads every subject in their class and looks after its roster."
        >
          {({ id }) => (
            <Select
              id={id}
              value={value.classTeacherOf ?? ''}
              onChange={(e) => onChange({ ...value, classTeacherOf: e.target.value || null })}
            >
              <option value="">No class</option>
              {rooms.map((room) => (
                <option key={room.id} value={room.id}>
                  {room.name}
                  {room.classTeacher ? ` — now ${room.classTeacher.name}` : ''}
                </option>
              ))}
            </Select>
          )}
        </Field>
      )}
    </div>
  );
}

function AssignmentRow({
  rooms,
  offered,
  value,
  onChange,
  onRemove,
}: {
  rooms: ClassroomDTO[];
  offered: ReadonlySet<string>;
  value: TeacherAssignment;
  onChange: (next: TeacherAssignment) => void;
  onRemove: () => void;
}) {
  const room = rooms.find((each) => each.id === value.classroomId) ?? null;
  const { subjects } = useOfferedSubjects(
    room?.board ?? null,
    room?.grade ?? null,
    offered,
    room?.academicYear?.id ?? null,
  );

  return (
    <div className="flex flex-wrap items-start gap-2.5 rounded-lg border border-line px-3 py-2.5">
      <Field label="Class" className="min-w-[180px] flex-1">
        {({ id }) => (
          <Select
            id={id}
            value={value.classroomId}
            onChange={(e) =>
              onChange({ classroomId: e.target.value, subjectId: '', unitIds: null })
            }
          >
            <option value="">Choose…</option>
            {rooms.map((each) => (
              <option key={each.id} value={each.id}>
                {each.name}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <SubjectUnitsPicker
        subjects={subjects}
        disabled={!room}
        value={{ subjectId: value.subjectId, unitIds: value.unitIds }}
        onChange={(next) => onChange({ classroomId: value.classroomId, ...next })}
      />
      <button
        type="button"
        className="mt-7 shrink-0 text-xs font-semibold text-danger"
        onClick={onRemove}
      >
        Remove
      </button>
    </div>
  );
}
